import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, DatePicker, message, Modal, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPenToSquare,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

import { useGetRolesQuery } from "../../service/roleService";
import { useMultiPostSalaryMutation } from "../../service/salaryService";

import IconButton from "../../components/common/IconButton";
import Datatable from "../../components/common/Datatable";
import { monthFormat } from "./index.config";

function EmployeeSalaryTable({
  openEmployeeModal,
  employeeData = [],
  isError,
  isLoading,
  setDate,
  date,
  deleteRecord,
}) {
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const { data: roles = [] } = useGetRolesQuery();
  const [multiPost] = useMultiPostSalaryMutation();

  const formattedData =
    employeeData?.map((info) => ({ ...info, key: info._id })) || [];

  const getRoleName = (roleId) =>
    roles.find((r) => r.value === roleId)?.label || "-";

  const setMonth = (details, isPaid) => {
    const data = JSON.parse(JSON.stringify(details));
    delete data?._id;
    return { ...data, month: date, isPaid };
  };

  const handleSubmit = (isPaid, selectedKeys) => {
    setOpen(true);

    const result = formattedData
      .filter(({ key }) => selectedKeys.includes(key))
      .map((info) =>
        info?.paymentDetails
          ? { details: setMonth(info.paymentDetails, isPaid) }
          : info?.previousSlip
            ? { details: setMonth(info.previousSlip, isPaid) }
            : { employee: info }
      );

    setEmployees(result);
  };

  const handleContinue = () => {
    const payslips = employees.filter((e) => !e.employee).map((e) => e.details);
    multiPost({ payload: { payslips } })
      .unwrap()
      .then((info) => {
        message?.success(info?.message);
        setOpen(false);
        setEmployees([]);
      })
      .catch((err) => message?.error(err?.data?.message));
    setOpen(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "firstName",
      render: (firstName, { lastName }) => `${firstName} ${lastName}`,
    },
    {
      title: "Employee Id",
      dataIndex: ["employeeDetails", "empId"],
    },
    {
      title: "Gender",
      dataIndex: ["employeeDetails", "gender"],
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Department",
      dataIndex: ["employeeDetails", "department"],
    },
    {
      title: "Designation",
      dataIndex: "role",
      render: getRoleName,
    },
    {
      title: "Paid Salary",
      dataIndex: "isPaid",
      render: (isPaid) => (
        <Tag color={isPaid ? "green" : "blue"}>
          {isPaid ? "Paid" : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 140,
      render: (_, record) => (
        <div className="flex space-x-3">
          {!record?.paymentDetails ? (
            <IconButton
              title="Generate"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => openEmployeeModal(record, false)}
            />
          ) : (
            <>
              <IconButton
                title="Edit"
                icon={<FontAwesomeIcon icon={faPenToSquare} />}
                onClick={() => openEmployeeModal(record, true)}
              />
              <IconButton
                title="Delete"
                icon={<FontAwesomeIcon icon={faTrashCan} />}
                onClick={() => deleteRecord(record)}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  const renderAction = (selectedKeys) => {
    const selectedRecords = formattedData.filter(({ key }) =>
      selectedKeys.includes(key)
    );

    const hasUnpaid = selectedRecords.some((r) => !r.isPaid);
    const hasPaid = selectedRecords.some((r) => r.isPaid);

    const unpaid = employees.filter((e) => e.employee);
    const empCount = employees?.filter((info) => !info?.details)?.length;
    return (
      <div className="flex gap-2 items-center justify-center">
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={null}
          title="Confirmation"
          centered
        >
          {unpaid.length ? (
            <>
              <div>
                <strong>
                  This action will retrieve records from the previous month.
                </strong>
                The following employees do not have a payslip for that period.
                <strong>Are you sure you want to continue?</strong>
              </div>
              <Datatable
                columns={[
                  {
                    title: "Name",
                    dataIndex: "employee",
                    render: (employee) =>
                      `${employee?.firstName} ${employee?.lastName}`,
                  },
                  {
                    title: "Employee Id",
                    dataIndex: "employee",
                    render: (employee) => employee?.employeeDetails?.empId,
                  },
                ]}
                tableData={unpaid}
                pagination={{ pageSize: 3 }}
              />
            </>
          ) : (
            <div className="p-2">
              This action will retrieve records from the previous month. Are you
              sure you want to continue?
            </div>
          )}

          <div className="text-right mt-6">
            <Button
              type="primary"
              disabled={!!empCount}
              className="bg-blue-600"
              onClick={handleContinue}
            >
              Continue
            </Button>
          </div>
        </Modal>

        <DatePicker
          picker="month"
          className="w-52 p-2"
          format={monthFormat}
          defaultValue={dayjs(date)}
          onChange={(val) => setDate(val)}
          allowClear={false}
        />
        {hasUnpaid && (
          <Button
            type="primary"
            className="bg-blue-600 text-white"
            onClick={() => handleSubmit(true, selectedKeys)}
          >
            Mark as Paid
          </Button>
        )}
        {hasPaid && (
          <Button
            type="primary"
            className="bg-blue-600 text-white"
            onClick={() => handleSubmit(false, selectedKeys)}
          >
            Mark as Unpaid
          </Button>
        )}
      </div>
    );
  };

  return (
    <Datatable
      tableData={formattedData}
      columns={columns}
      checkBox
      renderActions={renderAction}
      title="Pay Slips"
      isError={isError}
      isLoading={isLoading}
      pagination={{ pageSize: 5 }}
    />
  );
}

EmployeeSalaryTable.propTypes = {
  openEmployeeModal: PropTypes.func.isRequired,
  employeeData: PropTypes.array,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  setDate: PropTypes.func.isRequired,
  date: PropTypes.string.isRequired,
  deleteRecord: PropTypes.func.isRequired,
};

export default EmployeeSalaryTable;
